import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import Card from '../../components/ui/Card'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import api from '../../services/api'

const CategoriesPage = () => {
  const { t } = useTranslation('common')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    icon: 'folder',
    keywords: []
  })

  const queryClient = useQueryClient()

  // Pobieranie kategorii
  const { data: categories, isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await api.get('/categories')
      return response.data.categories
    }
  })

  // Mutacja do tworzenia kategorii
  const createMutation = useMutation(
    async (categoryData) => {
      const response = await api.post('/categories', categoryData)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['categories'])
        setIsModalOpen(false)
        resetForm()
      }
    }
  )

  // Mutacja do edycji kategorii
  const updateMutation = useMutation(
    async ({ id, ...categoryData }) => {
      const response = await api.put(`/categories/${id}`, categoryData)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['categories'])
        setIsModalOpen(false)
        setEditingCategory(null)
        resetForm()
      }
    }
  )

  // Mutacja do usuwania kategorii
  const deleteMutation = useMutation(
    async (id) => {
      await api.delete(`/categories/${id}`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['categories'])
      }
    }
  )

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3B82F6',
      icon: 'folder',
      keywords: []
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const categoryData = {
      name: formData.name,
      color: formData.color,
      ...(formData.description && { description: formData.description }),
      ...(formData.icon && { icon: formData.icon }),
      ...(formData.keywords.length > 0 && { keywords: formData.keywords })
    }

    if (editingCategory) {
      updateMutation.mutate({ id: editingCategory.id, ...categoryData })
    } else {
      createMutation.mutate(categoryData)
    }
  }

  const handleEdit = (category) => {
    setEditingCategory(category)
    setFormData({
      name: category.name,
      description: category.description || '',
      color: category.color,
      icon: category.icon,
      keywords: category.keywords || []
    })
    setIsModalOpen(true)
  }

  const handleDelete = (id) => {
    if (window.confirm(t('categories.confirmDelete'))) {
      deleteMutation.mutate(id)
    }
  }

  const openCreateModal = () => {
    setEditingCategory(null)
    resetForm()
    setIsModalOpen(true)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <LoadingSpinner size="lg" text={t('categories.loading')} />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-red-600">{t('categories.loadingError')}: {error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('categories.subtitle')}
          </p>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <button
            onClick={openCreateModal}
            className="ml-3 inline-flex items-center rounded-md bg-[#1f9f93] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#167e75] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1f9f93]"
          >
            {t('categories.add')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {categories?.map((category) => (
          <Card key={category.id}>
            <Card.Body>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                    style={{ backgroundColor: category.color }}
                  >
                    <span className="text-lg">📁</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-[var(--color-text)]">{category.name}</h3>
                    {category.description && (
                      <p className="text-sm text-[var(--color-text-muted)]">{category.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(category)}
                    className="text-[#1f9f93] hover:text-[#167e75] text-sm"
                  >
                    {t('edit')}
                  </button>
                  {!category.is_system && (
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="text-red-600 hover:text-red-900 text-sm"
                    >
                      {t('delete')}
                    </button>
                  )}
                </div>
              </div>
              {category.keywords && category.keywords.length > 0 && (
                <div className="mt-3">
                  <div className="flex flex-wrap gap-1">
                    {category.keywords.map((keyword, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center rounded-md bg-[var(--color-table-header)] px-2 py-1 text-xs font-medium text-[var(--color-text-muted)]"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        ))}
      </div>

      {/* Modal do tworzenia/edycji kategorii */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-[var(--color-text-muted)] bg-opacity-75 transition-opacity" onClick={() => setIsModalOpen(false)}></div>

            <div className="relative transform overflow-hidden rounded-lg bg-[var(--color-surface)] px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <form onSubmit={handleSubmit}>
                <div>
                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold leading-6 text-[var(--color-text)]">
                      {editingCategory ? t('categories.editTitle') : t('categories.addTitle')}
                    </h3>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('categories.name')}
                        </label>
                        <input
                          type="text"
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="description" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('categories.description')}
                        </label>
                        <textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={3}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                        />
                      </div>

                      <div>
                        <label htmlFor="color" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('categories.color')}
                        </label>
                        <div className="mt-1 flex items-center space-x-3">
                          <input
                            type="color"
                            id="color"
                            value={formData.color}
                            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                            className="h-10 w-20 rounded-md border border-[var(--color-border)]"
                          />
                          <input
                            type="text"
                            value={formData.color}
                            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                            className="block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="inline-flex w-full justify-center rounded-md bg-[#1f9f93] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#167e75] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1f9f93] sm:col-start-2 disabled:opacity-50"
                  >
                    {createMutation.isLoading || updateMutation.isLoading ? t('saving') : (editingCategory ? t('save') : t('add'))}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-[var(--color-surface)] px-3 py-2 text-sm font-semibold text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] hover:bg-[var(--color-hover)] sm:col-start-1 sm:mt-0"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CategoriesPage
